import { zodResolver } from "@hookform/resolvers/zod";
import { roleLabels } from "@purlink/shared/permissions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AccountNav } from "~/components/account/account-nav";
import { PageHeader } from "~/components/shell/app-shell";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Avatar } from "~/components/ui/feedback";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { toast } from "~/components/ui/toaster";
import { api, unwrap } from "~/lib/api";
import { errorMessage, meQuery, useCurrentUser } from "~/lib/auth";
import { formatInstant } from "~/lib/format";

export const Route = createFileRoute("/_app/account/")({
  component: AccountPage,
});

const profileSchema = z.object({ name: z.string().trim().min(1, "Enter your name.").max(80) });

function AccountPage() {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const form = useForm<z.input<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    values: { name: user.name },
  });

  const save = useMutation({
    mutationFn: (name: string) => unwrap(api.api.me.$patch({ json: { name } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      toast.success("Profile updated.");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <>
      <PageHeader title="Account" description="Your profile and account settings." />
      <AccountNav />
      <div className="flex max-w-3xl flex-col gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Avatar name={user.name} size="lg" />
              <div>
                <CardTitle>{user.name}</CardTitle>
                <CardDescription>{user.email}</CardDescription>
              </div>
            </div>
            <Badge tone="primary">{roleLabels[user.role]}</Badge>
          </CardHeader>
          <form
            noValidate
            onSubmit={form.handleSubmit(({ name }) => save.mutate(name.trim()))}
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
          >
            <Field label="Name" error={form.formState.errors.name?.message} className="flex-1">
              <Input autoComplete="name" {...form.register("name")} />
            </Field>
            <Button type="submit" loading={save.isPending} disabled={!form.formState.isDirty}>
              Save
            </Button>
          </form>
          <dl className="mt-5 grid gap-3 border-t border-border pt-5 text-small sm:grid-cols-2">
            <div>
              <dt className="text-fg-muted">Email</dt>
              <dd className="text-fg">{user.email}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">Member since</dt>
              <dd className="text-fg">{formatInstant(user.createdAt, "d MMMM yyyy")}</dd>
            </div>
          </dl>
        </Card>
      </div>
    </>
  );
}
